import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  isRatingAlreadyRecordedError,
  useSubmitOnlineOfferRatingMutation,
} from '../../api/onlineOfferApi';
import {OnlineOfferAfterSaleRatingResponseBody} from '../../api/types/onlineOfferTypes';
import {queryParams} from '../../consts/queryParams';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {getWorkspaceFromUri} from '../../utils/getWorkspaceFromUri';
import {parseRatingQueryParam} from '../../utils/parseRatingQueryParam';
import {useQueryState} from '../../utils/useQueryState';

type SubmittedRating = Omit<OnlineOfferAfterSaleRatingResponseBody, 'submittedAt'>;
type RatingCardState =
  {type: 'form'} | ({type: 'submitted'} & SubmittedRating) | {type: 'alreadyRated'};

export function useRatingCard() {
  const {afterSale, owner} = useGetOnlineOfferData();
  const {offerId} = useParams<{offerId: string}>();
  const {workspace} = getWorkspaceFromUri();
  const [queryRating] = useQueryState(queryParams.RATING);
  const [rating, setRating] = useState(() => parseRatingQueryParam(queryRating));
  const [recommendation, setRecommendation] = useState<number | null>(null);
  const [comment, setComment] = useState<string | null>(null);
  const [submittedRating, setSubmittedRating] = useState<SubmittedRating | null>(null);
  const [alreadyRated, setAlreadyRated] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [submitRating, {isLoading}] = useSubmitOnlineOfferRatingMutation();

  const savedRating = afterSale?.rating ?? submittedRating;
  const state: RatingCardState = match({savedRating, alreadyRated})
    .with({savedRating: Pattern.nonNullable}, ({savedRating: saved}) => ({
      type: 'submitted' as const,
      ...saved,
    }))
    .with({alreadyRated: true}, () => ({type: 'alreadyRated' as const}))
    .otherwise(() => ({type: 'form' as const}));

  const submit = () => {
    if (isNil(rating) || isNil(offerId) || isNil(workspace) || isLoading || state.type !== 'form') {
      return;
    }

    const answer = {rating, recommendation, comment: comment?.trim() || null};
    setHasError(false);
    submitRating({offerId, workspace, ...answer})
      .unwrap()
      .then(() => setSubmittedRating(answer))
      .catch((error: unknown) => {
        if (isRatingAlreadyRecordedError(error)) {
          setAlreadyRated(true);
        } else {
          setHasError(true);
        }
      });
  };

  return {
    state,
    owner,
    rating,
    setRating,
    recommendation,
    setRecommendation,
    comment,
    setComment,
    isLoading,
    hasError,
    submit,
  };
}
